import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import api, { formatApiError } from '../services/api';

const TeamsPage = () => {
  const { t } = useTranslation(['admin', 'common']);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [managerFilter, setManagerFilter] = useState('');
  
  const [teams, setTeams] = useState([]);
  const [teamsCount, setTeamsCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [teamsError, setTeamsError] = useState(null);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'
  
  const [centres, setCentres] = useState([]);
  const [loadingCentres, setLoadingCentres] = useState(false);
  const [managers, setManagers] = useState([]);
  const [loadingManagers, setLoadingManagers] = useState(false);
  
  // State for add team modal
  const [showAddTeamModal, setShowAddTeamModal] = useState(false);
  const [addFormData, setAddFormData] = useState({
    name: '',
    manager_ids: [],
    description: ''
  });
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState(null);
  const [addSuccess, setAddSuccess] = useState(false);

  // Form state for editing team
  const [editFormData, setEditFormData] = useState({
    name: '',
    manager_ids: []
  });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Delete team modal (when team has employees: choose assign to team or leave without team)
  const [showDeleteTeamModal, setShowDeleteTeamModal] = useState(false);
  const [teamToDelete, setTeamToDelete] = useState(null); // { id, name, memberCount }
  const [deleteChoice, setDeleteChoice] = useState('unassign'); // 'assign' | 'unassign'
  const [deleteTargetTeamId, setDeleteTargetTeamId] = useState('');
  const [otherTeamsForDelete, setOtherTeamsForDelete] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  // Managers dropdown-with-input (add form)
  const [addManagersOpen, setAddManagersOpen] = useState(false);
  const [addManagersQuery, setAddManagersQuery] = useState('');
  // Managers dropdown-with-input (edit form)
  const [editManagersOpen, setEditManagersOpen] = useState(false);
  const [editManagersQuery, setEditManagersQuery] = useState('');

  const getManagerLabel = (m) => `${(m.first_name || '').trim()} ${(m.last_name || '').trim()}`.trim() || m.email || '';

  const filterManagersByQuery = (list, q) => {
    const qq = (q || '').trim().toLowerCase();
    if (!qq) return list;
    return list.filter((m) => {
      const label = getManagerLabel(m).toLowerCase();
      return label.includes(qq) || (m.email && m.email.toLowerCase().includes(qq));
    });
  };
  const addFilteredManagers = filterManagersByQuery(managers, addManagersQuery);
  const editFilteredManagers = filterManagersByQuery(managers, editManagersQuery);

  // Map API team data to display format
  const mapTeamToDisplay = (team) => {
    const managersList = team.managers && Array.isArray(team.managers) ? team.managers : [];
    const managerNames = managersList.length
      ? managersList.map(m => `${m.first_name || ''} ${m.last_name || ''}`.trim() || m.email).filter(Boolean).join(', ')
      : t('teams.noManager');
    return {
      id: team.id,
      name: team.name || t('teams.unnamedTeam'),
      managers: managerNames,
      managerIds: managersList.map(m => m.id),
      memberCount: team.member_count || 0,
      rawData: team // Keep original data for detail panel
    };
  };

  const selectedTeamData = teams.find(t => t.id === selectedTeam);

  // Initialize form data when team is selected
  useEffect(() => {
    if (selectedTeamData?.rawData) {
      const teamData = selectedTeamData.rawData;
      const managerIds = (teamData.managers && Array.isArray(teamData.managers))
        ? teamData.managers.map(m => m.id)
        : [];
      setEditFormData({
        name: teamData.name || '',
        manager_ids: managerIds
      });
      setEditManagersQuery('');
      setEditManagersOpen(false);
      setSaveError(null);
      setSaveSuccess(false);
    }
  }, [selectedTeamData]);

  // Fetch teams from backend
  const fetchTeams = async () => {
    setLoadingTeams(true);
    setTeamsError(null);
    try {
      const filters = {};
      if (managerFilter) {
        // If managerFilter is 'none', send 'null' to backend to filter teams without managers
        filters.manager = managerFilter === 'none' ? 'null' : managerFilter;
      }
      if (searchQuery) filters.name = searchQuery; // Backend supports name search

      const response = await api.getTeams(filters, currentPage, pageSize);
      
      // Handle paginated response structure
      const teamsArray = Array.isArray(response) 
        ? response 
        : (response && response.results && Array.isArray(response.results)) 
          ? response.results 
          : [];
      
      const mappedTeams = teamsArray.map(mapTeamToDisplay);
      setTeams(mappedTeams);
      
      // Update pagination metadata
      if (response && typeof response === 'object' && !Array.isArray(response)) {
        setTeamsCount(response.count || 0);
      } else {
        setTeamsCount(mappedTeams.length);
      }
      
      // Clear selection if selected team is not in current results
      if (selectedTeam && !mappedTeams.find(t => t.id === selectedTeam)) {
        setSelectedTeam(null);
      }
    } catch (error) {
      const formattedError = formatApiError(error);
      setTeamsError(formattedError.generalError || t('teams.loadFailed'));
      console.error('Error fetching teams:', error);
    } finally {
      setLoadingTeams(false);
    }
  };

  // Fetch centres from backend
  useEffect(() => {
    const fetchCentres = async () => {
      setLoadingCentres(true);
      try {
        const response = await api.getCentresFilter();
        setCentres(Array.isArray(response) ? response : []);
      } catch (error) {
        console.error('Error fetching centres:', error);
      } finally {
        setLoadingCentres(false);
      }
    };

    fetchCentres();
  }, []);

  // Fetch managers from backend
  useEffect(() => {
    const fetchManagers = async () => {
      setLoadingManagers(true);
      try {
        const response = await api.getUsers({ role: 'MANAGER', status: 'active' }, 1, 1000);
        setManagers(response.results || []);
      } catch (error) {
        console.error('Error fetching managers:', error);
      } finally {
        setLoadingManagers(false);
      }
    };

    fetchManagers();
  }, []);

  // Fetch teams when filters change
  useEffect(() => {
    fetchTeams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [managerFilter]);

  // Handle Escape key to close panel
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (selectedTeam) {
          setSelectedTeam(null);
        }
        if (showAddTeamModal) {
          handleCloseAddModal();
        }
        if (showDeleteTeamModal) {
          handleCloseDeleteModal();
        }
      }
    };

    if (selectedTeam || showAddTeamModal || showDeleteTeamModal) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [selectedTeam, showAddTeamModal, showDeleteTeamModal]);

  const handleOpenAddModal = () => {
    setAddFormData({
      name: '',
      manager_ids: [],
      description: ''
    });
    setAddManagersQuery('');
    setAddManagersOpen(false);
    setAddError(null);
    setAddSuccess(false);
    setShowAddTeamModal(true);
  };

  const handleCloseAddModal = () => {
    setShowAddTeamModal(false);
    setAddError(null);
    setAddSuccess(false);
  };

  const handleAddFormChange = (field, value) => {
    setAddFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setAddError(null);
    setAddSuccess(false);
  };

  const handleAddTeam = async () => {
    // Validate required fields
    if (!addFormData.name || addFormData.name.trim() === '') {
      setAddError(t('teams.nameRequired'));
      return;
    }
    
    setAdding(true);
    setAddError(null);
    setAddSuccess(false);

    try {
      // Prepare create data
      const createData = {
        name: addFormData.name.trim(),
      };

      if (addFormData.manager_ids && addFormData.manager_ids.length > 0) {
        createData.manager_ids = addFormData.manager_ids.map(id => parseInt(id, 10)).filter(n => !Number.isNaN(n));
      }

      if (addFormData.description && addFormData.description.trim() !== '') {
        createData.description = addFormData.description.trim();
      }

      await api.createTeam(createData);
      
      // Refresh teams list
      await fetchTeams();
      
      // Close modal and reset form
      setShowAddTeamModal(false);
      setAddFormData({
        name: '',
        manager_ids: [],
        description: ''
      });
      setAddSuccess(true);
      setTimeout(() => setAddSuccess(false), 3000);
    } catch (error) {
      const formattedError = formatApiError(error);
      setAddError(formattedError.generalError || t('teams.createFailed'));
      console.error('Error creating team:', error);
    } finally {
      setAdding(false);
    }
  };

  const handleOpenDeleteModal = async (team) => {
    setTeamToDelete({ id: team.id, name: team.name, memberCount: team.memberCount || 0 });
    setDeleteError(null);
    setDeleteChoice('unassign');
    setDeleteTargetTeamId('');
    if ((team.memberCount || 0) > 0) {
      try {
        const allTeams = await api.getTeamsFilter();
        const list = Array.isArray(allTeams) ? allTeams : [];
        setOtherTeamsForDelete(list.filter((t) => t.id !== team.id));
      } catch (err) {
        console.error('Error fetching teams for delete dropdown:', err);
        setOtherTeamsForDelete([]);
      }
    } else {
      setOtherTeamsForDelete([]);
    }
    setShowDeleteTeamModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteTeamModal(false);
    setTeamToDelete(null);
    setDeleteError(null);
  };

  const handleConfirmDelete = async () => {
    if (!teamToDelete) return;
    if (teamToDelete.memberCount > 0 && deleteChoice === 'assign' && (!deleteTargetTeamId || deleteTargetTeamId === '')) {
      setDeleteError(t('teams.selectTeamToAssign'));
      return;
    }
    setDeleting(true);
    setDeleteError(null);
    try {
      const options = teamToDelete.memberCount > 0 && deleteChoice === 'assign' && deleteTargetTeamId
        ? { target_team_id: parseInt(deleteTargetTeamId, 10) }
        : {};
      await api.deleteTeam(teamToDelete.id, options);
      await fetchTeams();
      if (selectedTeam === teamToDelete.id) {
        setSelectedTeam(null);
      }
      handleCloseDeleteModal();
    } catch (error) {
      const formattedError = formatApiError(error);
      setDeleteError(formattedError.generalError || t('teams.deleteFailed'));
      console.error('Error deleting team:', error);
    } finally {
      setDeleting(false);
    }
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setManagerFilter('');
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortedTeams = () => {
    if (!sortColumn) return teams;

    return [...teams].sort((a, b) => {
      let aValue, bValue;

      switch (sortColumn) {
        case 'name':
          aValue = (a.name || '').toLowerCase();
          bValue = (b.name || '').toLowerCase();
          break;
        case 'member_count':
          aValue = a.memberCount || 0;
          bValue = b.memberCount || 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const SortableHeader = ({ column, children }) => {
    const isActive = sortColumn === column;
    return (
      <th 
        className="px-6 py-3 font-semibold cursor-pointer hover:bg-slate-100 transition-colors select-none"
        onClick={() => handleSort(column)}
      >
        <div className="flex items-center gap-2">
          <span>{children}</span>
          <div className="flex flex-col">
            <span className={`material-symbols-outlined text-[14px] leading-none ${
              isActive && sortDirection === 'asc' ? 'text-primary' : 'text-slate-300'
            }`}>
              arrow_drop_up
            </span>
            <span className={`material-symbols-outlined text-[14px] leading-none -mt-1 ${
              isActive && sortDirection === 'desc' ? 'text-primary' : 'text-slate-300'
            }`}>
              arrow_drop_down
            </span>
          </div>
        </div>
      </th>
    );
  };

  const handleFormChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setSaveError(null);
    setSaveSuccess(false);
  };

  const handleSaveTeam = async () => {
    if (!selectedTeam) return;

    // Validate required fields
    if (!editFormData.name || editFormData.name.trim() === '') {
      setSaveError(t('teams.nameRequired'));
      return;
    }
    
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      // Prepare update data
      const updateData = {
        name: editFormData.name.trim(),
      };

      if (editFormData.manager_ids && Array.isArray(editFormData.manager_ids)) {
        updateData.manager_ids = editFormData.manager_ids.map(id => parseInt(id, 10)).filter(n => !Number.isNaN(n));
      } else {
        updateData.manager_ids = [];
      }

      await api.updateTeam(selectedTeam, updateData);
      
      // Refresh teams list
      await fetchTeams();
      
      // Close the selected team panel
      setSelectedTeam(null);
      
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      const formattedError = formatApiError(error);
      setSaveError(formattedError.generalError || t('teams.saveFailed'));
      console.error('Error updating team:', error);
    } finally {
      setSaving(false);
    }
  };

  const startIndex = (currentPage - 1) * pageSize + 1;
  const endIndex = Math.min(currentPage * pageSize, teamsCount);
  const totalPages = Math.ceil(teamsCount / pageSize);

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show first page
      pages.push(1);
      
      if (currentPage > 3) {
        pages.push('...');
      }
      
      // Show pages around current page
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      
      for (let i = start; i <= end; i++) {
        if (i !== 1 && i !== totalPages) {
          pages.push(i);
        }
      }
      
      if (currentPage < totalPages - 2) {
        pages.push('...');
      }
      
      // Show last page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  // Get sorted teams for display
  const sortedTeams = getSortedTeams();

  return (
    <div className="flex flex-col gap-6">
      {/* Page Heading */}
      <div className="flex flex-wrap justify-between items-end gap-4 px-1">
        <div className="flex min-w-72 flex-col gap-2">
          <h1 className="text-slate-900 text-3xl font-bold leading-tight tracking-[-0.02em]">
            {t('teams.title')}
          </h1>
          <p className="text-slate-500 text-base font-normal leading-normal">
            {t('teams.subtitle')}
          </p>
        </div>
        <div className="flex gap-3">
          <button 
            onClick={handleOpenAddModal}
            className="flex items-center justify-center gap-2 rounded-lg h-10 px-4 bg-primary text-white text-sm font-bold shadow-md hover:bg-primary-hover transition-colors"
          >
            <span className="material-symbols-outlined text-[20px]">add</span>
            <span className="truncate">{t('teams.addNewTeam')}</span>
          </button>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm flex flex-col gap-4">
        <div className="flex flex-wrap items-end gap-4">
          <label className="flex flex-col min-w-64 flex-1">
            <p className="text-slate-700 text-xs font-semibold uppercase tracking-wider pb-2">
              {t('teams.searchLabel')}
            </p>
            <div className="relative">
              <input
                className="form-input w-full rounded-lg text-slate-900 dark:text-slate-100 focus:outline-0 focus:ring-2 focus:ring-primary/50 border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 h-10 placeholder:text-slate-400 dark:placeholder-slate-400 pl-10 pr-4 text-sm"
                placeholder={t('teams.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <span className="material-symbols-outlined absolute left-3 top-2.5 text-slate-400 text-[20px]">search</span>
            </div>
          </label>
            <div className="flex gap-3 flex-wrap items-end">
            <div className="flex flex-col gap-2">
              <p className="text-slate-700 text-xs font-semibold uppercase tracking-wider">{t('teams.manager')}</p>
              <div className="relative">
                <select
                  value={managerFilter}
                  onChange={(e) => setManagerFilter(e.target.value)}
                  disabled={loadingManagers}
                  className="h-10 appearance-none rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 px-4 pr-10 min-w-[140px] text-slate-700 dark:text-slate-100 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="">{t('teams.filterAllManagers')}</option>
                  <option value="none">{t('teams.noManager')}</option>
                  {managers.map((manager) => (
                    <option key={manager.id} value={manager.id.toString()}>
                      {`${manager.first_name || ''} ${manager.last_name || ''}`.trim() || manager.email}
                    </option>
                  ))}
                </select>
                <span className="material-symbols-outlined absolute right-3 top-2.5 text-slate-500 text-[20px] pointer-events-none">arrow_drop_down</span>
              </div>
            </div>
            <button
              onClick={handleClearFilters}
              className="h-10 px-4 text-sm font-medium text-primary hover:text-blue-700 transition-colors ml-2"
            >
              {t('common:clearAllFilters')}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 gap-6 items-start">
        {/* Teams Table */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h3 className="text-lg font-bold text-slate-900">{t('teams.title')}</h3>
              <span className="text-xs font-medium bg-slate-100 text-slate-600 px-2 py-1 rounded dark:bg-slate-700 dark:text-slate-300">
                {t('teams.count', { count: teamsCount })}
              </span>
            </div>
            <div className="overflow-x-auto">
            {teamsError ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-red-500">{teamsError}</div>
              </div>
            ) : sortedTeams.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-slate-500">{t('teams.noTeams')}</div>
              </div>
            ) : (
              <table className="w-full text-left text-sm whitespace-nowrap">
                <thead className="bg-slate-50 text-slate-500 border-b border-slate-100">
                  <tr>
                    <SortableHeader column="name">{t('common:team')}</SortableHeader>
                    <th className="px-6 py-3 font-semibold">{t('teams.manager')}</th>
                    <SortableHeader column="member_count">{t('teams.members')}</SortableHeader>
                    <th className="px-6 py-3 font-semibold text-right">{t('common:actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {sortedTeams.map((team) => (
                  <motion.tr
                    key={team.id}
                    className={`hover:bg-slate-50 transition-colors group cursor-pointer border-l-4 ${
                      selectedTeam === team.id
                        ? 'bg-primary/5'
                        : 'border-l-transparent'
                    }`}
                    onClick={() => {
                      setSelectedTeam(selectedTeam === team.id ? null : team.id);
                    }}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                          <span className="material-symbols-outlined text-[20px]">groups</span>
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900">{team.name}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-slate-600 text-sm">
                        {team.managers && team.managers !== t('teams.noManager') ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/40 dark:text-blue-200 dark:border-blue-700/70">
                            <span className="material-symbols-outlined text-[14px]">manage_accounts</span>
                            {team.managers}
                          </span>
                        ) : (
                          <span className="text-slate-400">{t('teams.noManager')}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-700/60 dark:text-slate-200 dark:border-slate-600/80">
                        {team.memberCount || 0}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2.5">
                        {/* Edit Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedTeam(selectedTeam === team.id ? null : team.id);
                          }}
                          className="h-9 px-4 rounded-lg text-xs font-semibold flex items-center gap-2 shadow-sm bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 dark:bg-blue-900/40 dark:text-blue-200 dark:border-blue-700/70 dark:hover:bg-blue-900/60 transition-colors"
                          title={t('teams.editTeam')}
                        >
                          <span className="material-symbols-outlined text-[18px]">
                            edit
                          </span>
                          <span className="font-medium">{t('common:edit')}</span>
                        </button>
                        {/* Delete Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenDeleteModal(team);
                          }}
                          className="h-9 px-4 rounded-lg text-xs font-semibold flex items-center gap-2 shadow-sm bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-200 dark:border-red-700/70 dark:hover:bg-red-900/50 transition-colors"
                          title={t('teams.deleteTeam')}
                        >
                          <span className="material-symbols-outlined text-[18px]">delete</span>
                          <span className="font-medium">{t('common:delete')}</span>
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                  ))}
                </tbody>
              </table>
            )}
            </div>
            {/* Pagination */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-6 py-4 border-t border-slate-100 bg-slate-50/50">
              <div className="flex items-center gap-4">
                <span className="text-sm text-slate-500">
                  {t('common:showingRange', { from: teamsCount > 0 ? startIndex : 0, to: endIndex, total: teamsCount })}
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500">{t('common:perPage')}</span>
                  <div className="relative">
                    <select
                      value={pageSize}
                      onChange={(e) => setPageSize(Number(e.target.value))}
                      className="appearance-none rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-1.5 pr-8 text-sm text-slate-900 dark:text-slate-100 focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <span className="material-symbols-outlined absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 text-[16px] pointer-events-none">arrow_drop_down</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                  className="h-8 w-8 flex items-center justify-center rounded border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title={t('common:firstPage')}
                >
                  <span className="material-symbols-outlined text-sm">first_page</span>
                </button>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="h-8 w-8 flex items-center justify-center rounded border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title={t('common:previousPage')}
                >
                  <span className="material-symbols-outlined text-sm">chevron_left</span>
                </button>
                
                {/* Page numbers */}
                <div className="flex items-center gap-1">
                  {getPageNumbers().map((page, index) => (
                    page === '...' ? (
                      <span key={`ellipsis-${index}`} className="px-2 text-slate-400">...</span>
                    ) : (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`h-8 w-8 flex items-center justify-center rounded border text-sm font-medium transition-colors ${
                          currentPage === page
                            ? 'bg-primary text-white border-primary'
                            : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700'
                        }`}
                      >
                        {page}
                      </button>
                    )
                  ))}
                </div>
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                  className="h-8 w-8 flex items-center justify-center rounded border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title={t('common:nextPage')}
                >
                  <span className="material-symbols-outlined text-sm">chevron_right</span>
                </button>
                <button
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage >= totalPages}
                  className="h-8 w-8 flex items-center justify-center rounded border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title={t('common:lastPage')}
                >
                  <span className="material-symbols-outlined text-sm">last_page</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Team Modal */}
      <AnimatePresence>
        {showAddTeamModal && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/50 z-50"
              onClick={handleCloseAddModal}
            />
            {/* Modal */}
            <div onClick={handleCloseAddModal} className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ duration: 0.2 }}
                className="bg-white rounded-xl shadow-xl max-w-2xl w-full border border-slate-200 my-8 max-h-[90vh] overflow-y-auto"
                onClick={(e) => {
                  if (e.target === e.currentTarget) {
                    handleCloseAddModal();
                  }
                }}
              >
                <div 
                  className="min-h-full"
                  onClick={(e) => e.stopPropagation()}
                >
                <div className="p-6 border-b border-slate-100 flex justify-between items-start sticky top-0 bg-white z-10 relative">
                  <div className="flex-1 pr-12">
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">{t('teams.createNewEyebrow')}</p>
                    <h3 className="text-xl font-bold text-slate-900">{t('teams.addTeam')}</h3>
                    <p className="text-sm text-slate-500">
                      {t('teams.createSubtitle')}
                    </p>
                  </div>
                  <button
                    onClick={handleCloseAddModal}
                    className="absolute top-6 right-6 h-8 w-8 flex items-center justify-center rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-all duration-200 group focus:outline-none focus:ring-2 focus:ring-primary/20 focus:ring-offset-1"
                    title={t('common:closeModal')}
                  >
                    <span className="material-symbols-outlined text-[22px] group-hover:scale-110 transition-transform duration-200">close</span>
                  </button>
                </div>
                <div className="p-6 space-y-6">
              {/* Success/Error Messages */}
              {addSuccess && (
                <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700/50 dark:text-emerald-200 text-sm">
                  {t('teams.created')}
                </div>
              )}
              {addError && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 dark:bg-red-900/30 dark:border-red-700/50 dark:text-red-200 text-sm">
                  {addError}
                </div>
              )}

              {/* Team Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2 md:col-span-2">
                  <label className="text-sm font-semibold text-slate-900 block">{t('teams.name')} *</label>
                  <input
                    type="text"
                    value={addFormData.name}
                    onChange={(e) => handleAddFormChange('name', e.target.value)}
                    className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 p-3 text-sm text-slate-900 dark:text-slate-100 focus:border-primary focus:ring-1 focus:ring-primary outline-none placeholder-slate-500 dark:placeholder-slate-400"
                    placeholder={t('teams.namePlaceholder')}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-900 dark:text-slate-100 block">{t('teams.managers')}</label>
                  <div className="relative">
                    <div
                      className={`w-full min-h-[42px] rounded-lg border text-sm focus-within:ring-1 focus-within:ring-primary focus-within:border-primary outline-none flex flex-wrap items-center gap-2 p-2 ${addManagersOpen ? 'border-primary ring-1 ring-primary' : 'border-slate-200 dark:border-slate-600'} bg-slate-50 text-slate-900 dark:bg-slate-800 dark:text-slate-100`}
                    >
                      {(addFormData.manager_ids || []).map((id) => {
                        const m = managers.find((x) => x.id === id);
                        if (!m) return null;
                        return (
                          <span
                            key={m.id}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-900/40 dark:text-blue-200 dark:border-blue-700/70"
                          >
                            {getManagerLabel(m)}
                            <button
                              type="button"
                              onClick={() => handleAddFormChange('manager_ids', (addFormData.manager_ids || []).filter((x) => x !== id))}
                              className="hover:bg-blue-100 dark:hover:bg-blue-800/60 rounded p-0.5"
                              aria-label={t('common:remove')}
                            >
                              <span className="material-symbols-outlined text-[14px]">close</span>
                            </button>
                          </span>
                        );
                      })}
                      <input
                        type="text"
                        value={addManagersQuery}
                        onChange={(e) => {
                          setAddManagersQuery(e.target.value);
                          setAddManagersOpen(true);
                        }}
                        onFocus={() => setAddManagersOpen(true)}
                        onBlur={() => setTimeout(() => setAddManagersOpen(false), 180)}
                        placeholder={(addFormData.manager_ids || []).length ? t('teams.addAnotherManager') : t('teams.searchManagers')}
                        disabled={loadingManagers}
                        className="flex-1 min-w-[120px] bg-transparent border-0 py-1.5 px-1 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-0"
                      />
                      <span className="material-symbols-outlined text-slate-400 dark:text-slate-500 text-[20px] self-center">arrow_drop_down</span>
                    </div>
                    {addManagersOpen && (
                      <ul
                        className="absolute z-20 mt-1 w-full max-h-48 overflow-auto rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-lg py-1 text-sm"
                        onMouseDown={(e) => e.preventDefault()}
                      >
                        {addFilteredManagers.map((m) => {
                          const selected = (addFormData.manager_ids || []).includes(m.id);
                          return (
                            <li
                              key={m.id}
                              onMouseDown={() => {
                                if (selected) {
                                  handleAddFormChange('manager_ids', (addFormData.manager_ids || []).filter((x) => x !== m.id));
                                } else {
                                  handleAddFormChange('manager_ids', [...(addFormData.manager_ids || []), m.id]);
                                }
                                setAddManagersQuery('');
                              }}
                              className={`px-3 py-2 cursor-pointer flex items-center justify-between ${selected ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200' : 'hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-900 dark:text-slate-100'}`}
                            >
                              <span>{getManagerLabel(m) || m.email}</span>
                              {selected && <span className="material-symbols-outlined text-[18px]">check</span>}
                            </li>
                          );
                        })}
                        {addFilteredManagers.length === 0 && (
                          <li className="px-3 py-2 text-slate-500 dark:text-slate-400">{t('teams.noManagersMatch')}</li>
                        )}
                      </ul>
                    )}
                  </div>
                </div>

                <div className="space-y-2 md:col-span-2">
                  <label className="text-sm font-semibold text-slate-900 block">{t('common:description')}</label>
                  <textarea
                    value={addFormData.description}
                    onChange={(e) => handleAddFormChange('description', e.target.value)}
                    className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 p-3 text-sm text-slate-900 dark:text-slate-100 focus:border-primary focus:ring-1 focus:ring-primary outline-none resize-none placeholder-slate-500 dark:placeholder-slate-400"
                    placeholder={t('teams.descriptionPlaceholder')}
                    rows={3}
                  />
                </div>
              </div>

              {/* Create Button */}
              <div className="flex gap-3 pt-4 border-t border-slate-100">
                <button
                  onClick={handleAddTeam}
                  disabled={adding}
                  className="flex-1 bg-primary hover:bg-primary-hover text-white px-4 py-2.5 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <span className="material-symbols-outlined text-[18px]">add</span>
                  {t('teams.createTeam')}
                </button>
                <button
                  onClick={handleCloseAddModal}
                  className="px-4 py-2.5 border border-slate-200 text-slate-700 rounded-lg font-medium hover:bg-slate-50 transition-colors"
                >
                  {t('common:cancel')}
                </button>
              </div>
                </div>
                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>

      {/* Team Edit Modal */}
      <AnimatePresence>
        {selectedTeam && selectedTeamData && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/50 z-50"
              onClick={() => setSelectedTeam(null)}
            />
            {/* Modal */}
            <div onClick={() => setSelectedTeam(null)} className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ duration: 0.2 }}
                className="bg-white rounded-xl shadow-xl max-w-2xl w-full border border-slate-200 my-8 max-h-[90vh] overflow-y-auto"
                onClick={(e) => {
                  if (e.target === e.currentTarget) {
                    setSelectedTeam(null);
                  }
                }}
              >
                <div 
                  className="min-h-full"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="p-6 border-b border-slate-100 flex justify-between items-start sticky top-0 bg-white z-10 relative">
                    <div className="flex-1 pr-12">
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">{t('teams.selectedTeam')}</p>
                      <h3 className="text-xl font-bold text-slate-900">{selectedTeamData.name}</h3>
                      <p className="text-sm text-slate-500">
                        {selectedTeamData.centre || t('teams.noCentreAssigned')}
                      </p>
                    </div>
                    <button
                      onClick={() => setSelectedTeam(null)}
                      className="absolute top-6 right-6 h-8 w-8 flex items-center justify-center rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-all duration-200 group focus:outline-none focus:ring-2 focus:ring-primary/20 focus:ring-offset-1"
                      title={t('common:closeModal')}
                    >
                      <span className="material-symbols-outlined text-[22px] group-hover:scale-110 transition-transform duration-200">close</span>
                    </button>
                  </div>
                  <div className="p-6 space-y-6">
              {/* Success/Error Messages */}
              {saveSuccess && (
                <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700/50 dark:text-emerald-200 text-sm">
                  {t('teams.updated')}
                </div>
              )}
              {saveError && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 dark:bg-red-900/30 dark:border-red-700/50 dark:text-red-200 text-sm">
                  {saveError}
                </div>
              )}

              {/* Team Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2 md:col-span-2">
                  <label className="text-sm font-semibold text-slate-900 block">{t('teams.name')} *</label>
                  <input
                    type="text"
                    value={editFormData.name}
                    onChange={(e) => handleFormChange('name', e.target.value)}
                    className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 p-3 text-sm text-slate-900 dark:text-slate-100 focus:border-primary focus:ring-1 focus:ring-primary outline-none placeholder-slate-500 dark:placeholder-slate-400"
                    placeholder={t('teams.namePlaceholder')}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-900 dark:text-slate-100 block">{t('teams.managers')}</label>
                  <div className="relative">
                    <div
                      className={`w-full min-h-[42px] rounded-lg border text-sm focus-within:ring-1 focus-within:ring-primary focus-within:border-primary outline-none flex flex-wrap items-center gap-2 p-2 ${editManagersOpen ? 'border-primary ring-1 ring-primary' : 'border-slate-200 dark:border-slate-600'} bg-slate-50 text-slate-900 dark:bg-slate-800 dark:text-slate-100`}
                    >
                      {(editFormData.manager_ids || []).map((id) => {
                        const m = managers.find((x) => x.id === id);
                        if (!m) return null;
                        return (
                          <span
                            key={m.id}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-900/40 dark:text-blue-200 dark:border-blue-700/70"
                          >
                            {getManagerLabel(m)}
                            <button
                              type="button"
                              onClick={() => handleFormChange('manager_ids', (editFormData.manager_ids || []).filter((x) => x !== id))}
                              className="hover:bg-blue-100 dark:hover:bg-blue-800/60 rounded p-0.5"
                              aria-label={t('common:remove')}
                            >
                              <span className="material-symbols-outlined text-[14px]">close</span>
                            </button>
                          </span>
                        );
                      })}
                      <input
                        type="text"
                        value={editManagersQuery}
                        onChange={(e) => {
                          setEditManagersQuery(e.target.value);
                          setEditManagersOpen(true);
                        }}
                        onFocus={() => setEditManagersOpen(true)}
                        onBlur={() => setTimeout(() => setEditManagersOpen(false), 180)}
                        placeholder={(editFormData.manager_ids || []).length ? t('teams.addAnotherManager') : t('teams.searchManagers')}
                        disabled={loadingManagers}
                        className="flex-1 min-w-[120px] bg-transparent border-0 py-1.5 px-1 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-0"
                      />
                      <span className="material-symbols-outlined text-slate-400 dark:text-slate-500 text-[20px] self-center">arrow_drop_down</span>
                    </div>
                    {editManagersOpen && (
                      <ul
                        className="absolute z-20 mt-1 w-full max-h-48 overflow-auto rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-lg py-1 text-sm"
                        onMouseDown={(e) => e.preventDefault()}
                      >
                        {editFilteredManagers.map((m) => {
                          const selected = (editFormData.manager_ids || []).includes(m.id);
                          return (
                            <li
                              key={m.id}
                              onMouseDown={() => {
                                if (selected) {
                                  handleFormChange('manager_ids', (editFormData.manager_ids || []).filter((x) => x !== m.id));
                                } else {
                                  handleFormChange('manager_ids', [...(editFormData.manager_ids || []), m.id]);
                                }
                                setEditManagersQuery('');
                              }}
                              className={`px-3 py-2 cursor-pointer flex items-center justify-between ${selected ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200' : 'hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-900 dark:text-slate-100'}`}
                            >
                              <span>{getManagerLabel(m) || m.email}</span>
                              {selected && <span className="material-symbols-outlined text-[18px]">check</span>}
                            </li>
                          );
                        })}
                        {editFilteredManagers.length === 0 && (
                          <li className="px-3 py-2 text-slate-500 dark:text-slate-400">{t('teams.noManagersMatch')}</li>
                        )}
                      </ul>
                    )}
                  </div>
                </div>
              </div>

                    {/* Save and Delete */}
                    <div className="flex gap-3 pt-4 border-t border-slate-100 flex-wrap">
                      <button
                        onClick={handleSaveTeam}
                        disabled={saving}
                        className="flex-1 min-w-[140px] bg-primary hover:bg-primary-hover text-white px-4 py-2.5 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        <span className="material-symbols-outlined text-[18px]">save</span>
                        {t('teams.saveChanges')}
                      </button>
                      <button
                        onClick={() => {
                          if (selectedTeamData) {
                            handleCloseDeleteModal();
                            setSelectedTeam(null);
                            handleOpenDeleteModal(selectedTeamData);
                          }
                        }}
                        className="px-4 py-2.5 rounded-lg font-medium flex items-center gap-2 bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-200 dark:border-red-700/50 dark:hover:bg-red-900/50 transition-colors"
                      >
                        <span className="material-symbols-outlined text-[18px]">delete</span>
                        {t('teams.deleteTeam')}
                      </button>
                      <button
                        onClick={() => setSelectedTeam(null)}
                        className="px-4 py-2.5 border border-slate-200 text-slate-700 rounded-lg font-medium hover:bg-slate-50 transition-colors"
                      >
                        {t('common:cancel')}
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>

      {/* Delete Team Modal (with optional employee reassignment) */}
      <AnimatePresence>
        {showDeleteTeamModal && teamToDelete && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/50 z-[60]"
              onClick={handleCloseDeleteModal}
            />
            <div onClick={handleCloseDeleteModal} className="fixed inset-0 z-[60] flex items-center justify-center p-4 overflow-y-auto">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ duration: 0.2 }}
                className="bg-white rounded-xl shadow-xl max-w-md w-full border border-slate-200 my-8"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6 border-b border-slate-100 flex justify-between items-start">
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">{t('teams.deleteTeam')}</p>
                    <h3 className="text-xl font-bold text-slate-900">{t('teams.deleteNamed', { name: teamToDelete.name })}</h3>
                  </div>
                  <button
                    onClick={handleCloseDeleteModal}
                    className="h-8 w-8 flex items-center justify-center rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                    title={t('common:closeEsc')}
                  >
                    <span className="material-symbols-outlined text-[22px]">close</span>
                  </button>
                </div>
                <div className="p-6 space-y-4">
                  {deleteError && (
                    <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 dark:bg-red-900/30 dark:border-red-700/50 dark:text-red-200 text-sm">
                      {deleteError}
                    </div>
                  )}
                  {teamToDelete.memberCount > 0 ? (
                    <>
                      <p className="text-slate-600 text-sm">
                        {t('teams.deleteHasMembers', { count: teamToDelete.memberCount })}
                      </p>
                      <div className="space-y-3">
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="radio"
                            name="deleteChoice"
                            checked={deleteChoice === 'assign'}
                            onChange={() => { setDeleteChoice('assign'); setDeleteError(null); }}
                            className="mt-1.5 rounded-full border-slate-300 text-primary focus:ring-primary"
                          />
                          <span className="text-sm font-medium text-slate-700">{t('teams.assignToAnotherTeam')}</span>
                        </label>
                        {deleteChoice === 'assign' && (
                          <div className="ml-6">
                            <select
                              value={deleteTargetTeamId}
                              onChange={(e) => { setDeleteTargetTeamId(e.target.value); setDeleteError(null); }}
                              className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 p-2.5 pr-10 text-sm text-slate-900 dark:text-slate-100 focus:border-primary focus:ring-1 focus:ring-primary outline-none cursor-pointer"
                            >
                              <option value="">{t('teams.selectTeam')}</option>
                              {otherTeamsForDelete.map((t) => (
                                <option key={t.id} value={t.id.toString()}>{t.name}</option>
                              ))}
                            </select>
                          </div>
                        )}
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="radio"
                            name="deleteChoice"
                            checked={deleteChoice === 'unassign'}
                            onChange={() => { setDeleteChoice('unassign'); setDeleteError(null); }}
                            className="mt-1.5 rounded-full border-slate-300 text-primary focus:ring-primary"
                          />
                          <span className="text-sm font-medium text-slate-700">{t('teams.unassignMembers')}</span>
                        </label>
                      </div>
                    </>
                  ) : (
                    <p className="text-slate-600 text-sm">{t('teams.confirmDelete')}</p>
                  )}
                </div>
                <div className="p-6 pt-0 flex gap-3 justify-end border-t border-slate-100">
                  <button
                    onClick={handleCloseDeleteModal}
                    className="px-4 py-2.5 border border-slate-200 text-slate-700 rounded-lg font-medium hover:bg-slate-50 transition-colors"
                  >
                    {t('common:cancel')}
                  </button>
                  <button
                    onClick={handleConfirmDelete}
                    disabled={deleting}
                    className="px-4 py-2.5 rounded-lg font-medium bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <span className="material-symbols-outlined text-[18px]">delete</span>
                    {deleting ? t('common:deleting') : t('teams.deleteTeam')}
                  </button>
                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TeamsPage;

